import { AangevraagdeVestigingOpeningstijd, BeheerVestiging, Vestiging } from "@/entities/vestiging";
import { Button } from "@/shared/components";
import { useCallback, useMemo, useState } from "react";
import { BaseFaciliteitenTable, StationHeader } from "../../shared";
import { createColumns } from "./columns-factory";
import { StopFaciliteitenDialog } from "./stop-faciliteiten-dialog";

interface FaciliteitenDetailsForStationProps {
    stationCode: string;
    vestigingen: Vestiging[];
    handlers: {
        onStopRequest: (vestigingen: Vestiging[]) => Promise<void>;
        onUpdateOpeningstijd: (vestiging: Vestiging, updatedOpeningstijden: AangevraagdeVestigingOpeningstijd[]) => Promise<void>;
    };
}

const columnWidths = ["45%", "17%", "35%", "3%"];
export function FaciliteitenDetailsForStation({ vestigingen, stationCode, handlers }: Readonly<FaciliteitenDetailsForStationProps>) {
    const [stopFaciliteitenDialogOpen, setStopFaciliteitenDialogOpen] = useState(false);

    const handleUpdateOpeningstijd = useCallback(
        async (vestiging: Vestiging, nieuwOpenTot: string) => {
            if (!vestiging.aangevraagdeVestiging) {
                throw new Error("Fout bij het updaten van openingstijden voor station: aangevraagdeVestiging is leeg");
            }

            const openingstijden: AangevraagdeVestigingOpeningstijd[] = [...vestiging.aangevraagdeVestiging.openingstijden];
            const dagVandaag = BeheerVestiging.getDayOfTheWeek(vestiging.aangevraagdeVestiging.aangevraagdOpDatumTijd);
            const beheerOpeningstijdenVandaag = BeheerVestiging.getOpeningstijdenVandaag(vestiging.beheerVestiging);
            const newAangepasteOpeningstijd: AangevraagdeVestigingOpeningstijd = {
                dag: dagVandaag,
                openVan: beheerOpeningstijdenVandaag.openVan,
                openTot: beheerOpeningstijdenVandaag.openTot,
                aangepasteOpenTot: nieuwOpenTot,
            };

            const indexForToday = vestiging.aangevraagdeVestiging.openingstijden.findIndex((item) => item.dag === dagVandaag);
            if (indexForToday !== -1) {
                openingstijden[indexForToday] = newAangepasteOpeningstijd;
            } else {
                openingstijden.push(newAangepasteOpeningstijd);
            }

            await handlers.onUpdateOpeningstijd(vestiging, openingstijden);
        },
        [handlers]
    );

    const handleStopRequest = async () => {
        await handlers.onStopRequest(vestigingen.filter((v) => Vestiging.canRequestStop(v)));
        setStopFaciliteitenDialogOpen(false);
    };

    const columns = useMemo(
        () =>
            createColumns({
                onStopRequest: (vestiging: Vestiging) => handlers.onStopRequest([vestiging]),
                onUpdateOpeningstijd: handleUpdateOpeningstijd,
            }),
        [handleUpdateOpeningstijd, handlers]
    );

    return (
        <div className="space-y-4">
            <StationHeader stationCode={stationCode}>
                {Vestiging.canRequestBulkStop(vestigingen) && (
                    <Button
                        variant="secondary"
                        size="s"
                        data-testid={`stop-vestigingen-${stationCode}`}
                        onClick={() => setStopFaciliteitenDialogOpen(true)}
                    >
                        Faciliteiten stoppen op {stationCode}
                    </Button>
                )}
            </StationHeader>

            <BaseFaciliteitenTable stationCode={stationCode} vestigingen={vestigingen} columns={columns} columnWidths={columnWidths} />
            <StopFaciliteitenDialog
                open={stopFaciliteitenDialogOpen}
                onOpenChange={setStopFaciliteitenDialogOpen}
                variant={{ type: "station", stationCode }}
                onConfirm={() => {
                    void handleStopRequest();
                }}
                onCancel={() => setStopFaciliteitenDialogOpen(false)}
            />
        </div>
    );
}
